import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Info } from 'lucide-react';
import { useState } from 'react';

import { describeField } from '../lib/field-descriptions';

/**
 * A hoverable "what is this field" marker, for the paths that have a
 * description worth reading.
 *
 * Renders NOTHING when the field has none — most of them — so the icon stays a
 * signal that there is something non-obvious here rather than decoration on
 * every row.
 *
 * Built on the Radix primitives rather than the app's shared `Tooltip`, for one
 * reason: the shared `TooltipContent` portals to `document.body`, which is
 * outside `.gr-root`. This page keeps its whole palette and its `data-theme` on
 * that element, so a bubble outside it would render in the app's tokens against
 * the review surface's dark chrome. Portalling into `.gr-root` instead puts the
 * bubble inside the theme while still escaping `.gr-insp-body`, which scrolls
 * and would otherwise clip it.
 */
export function FieldInfo({ path }: { path: string }) {
  const description = describeField(path);

  // The portal target is found from the trigger rather than queried globally,
  // so a second `.gr-root` (or none at all, as in a unit test) cannot send the
  // bubble to the wrong place.
  const [gapRoot, setGapRoot] = useState<HTMLElement | null>(null);

  if (description === undefined) return null;

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger
        className="gr-info"
        type="button"
        // Not a control: it opens nothing and changes nothing, so it announces
        // itself as the description it carries.
        aria-label={`${path}: ${description}`}
        ref={(trigger) =>
          setGapRoot(trigger?.closest<HTMLElement>('.gr-root') ?? null)
        }
        // The icon sits inside a `<summary>` in the raw tree, where any click
        // folds the branch away — including the click of somebody reaching for
        // the description.
        onClick={(clickEvent) => clickEvent.preventDefault()}
      >
        <Info aria-hidden />
      </TooltipPrimitive.Trigger>

      <TooltipPrimitive.Portal container={gapRoot}>
        <TooltipPrimitive.Content
          className="gr-tip"
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={12}
        >
          {description}
          <TooltipPrimitive.Arrow className="gr-tip-arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
